import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, type TokenSource } from './client.ts';
import { queryDefaults } from './queries.ts';
import { useSession } from '../auth/session.tsx';

/**
 * Contacts (Release 1; api/src/routes/contacts.ts). People, companies and the links that put them on a
 * client, a building or a property. Every edit carries one of the four reasons; the server decides who
 * may do what and says so in `may_*`, and every route asks again. Searches go in POST bodies, never a
 * URL, because App Service's own log records query strings.
 */

/**
 * An edit's reason: a code from migration 123's shared edit_reason table. The list is the server's
 * (useEditReasons), never one written here: a fifth reason is a decision made in that table.
 */
export type EditReason = string;
export interface EditReasonRow { code: string; label: string; needs_text: boolean }

/** One link: a person or company in a role on a client, a building or a property. */
export interface ContactLink {
  link_id: string;
  contact_id: string | null;
  organization_id: string | null;
  name: string | null;
  job_title: string | null;
  credentials: string | null;
  organization: string | null;
  role_code: string;
  role_label: string;
  category: string;
  relationship: string | null;
  phone: string | null;
  phone_secondary: string | null;
  email: string | null;
  email_secondary: string | null;
  do_not_contact: boolean;
  do_not_contact_note: string | null;
  is_primary: boolean;
  is_emergency: boolean;
  has_authority: boolean;
  authority_note: string | null;
  court_appointed: boolean;
  /** Whole for property and finance readers; the last four, masked, for everyone else. */
  account_ref: string | null;
  started_on: string | null;
  appointed_date: string | null;
  ended_on: string | null;
  is_current: boolean;
  notes: string | null;
  /** The link's version: sent back with an edit, so a stale form is refused. */
  version: string;
  end_reason: EditReason | null;
  end_reason_label: string | null;
  end_reason_text: string | null;
  /** On the contact page: where the link is. */
  client_id?: string | null;
  client_ref?: string | null;
  client_name?: string | null;
  facility_id?: string | null;
  facility_name?: string | null;
  property_id?: string | null;
  property_street?: string | null;
}

export interface Contact {
  id: string;
  last_name: string | null;
  first_name: string | null;
  full_name: string;
  organization_id: string | null;
  organization: string | null;
  job_title: string | null;
  credentials: string | null;
  phone_primary: string | null;
  phone_secondary: string | null;
  email: string | null;
  email_secondary: string | null;
  preferred_contact: 'Phone' | 'Email' | 'Text' | 'Mail' | null;
  language: string | null;
  is_active: boolean;
  do_not_contact: boolean;
  street_address: string | null;
  unit: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  merged_into: string | null;
  version: string;
  edits: number;
}

export interface HistoryEntry {
  version: number;
  as_first_recorded: boolean;
  reason: string | null;
  reason_text: string | null;
  recorded_at: string;
  recorded_by: string | null;
  changed: string[];
  fields: Record<string, unknown>;
}

export interface ContactRole {
  role_code: string;
  role_label: string;
  category: string;
  attaches_to_client: boolean;
  attaches_to_facility: boolean;
  attaches_to_property: boolean;
}

export interface Candidate {
  id: string;
  name: string;
  organization: string | null;
  job_title: string | null;
  phone: string | null;
  email: string | null;
  links: number;
  matched_on: ('phone' | 'email' | 'name')[];
}

export interface RoutingRow {
  link_id: string;
  contact_name: string | null;
  role_label: string;
  relationship: string | null;
  phone_primary: string | null;
  phone_secondary: string | null;
  email: string | null;
  email_secondary: string | null;
  do_not_contact: boolean;
  place_kind: 'client' | 'facility' | 'property';
  facility_name: string | null;
  client_id: string | null;
  client_ref: string | null;
  client_name: string | null;
  legal_status: string | null;
  case_manager: string | null;
  finance_case_manager: string | null;
  covering: string | null;
}

export type LinkTarget = { client_id: string } | { facility_id: string } | { property_id: string };

export interface NewPerson {
  last_name: string | null;
  first_name: string | null;
  organization_id?: string | null;
  job_title?: string | null;
  phone_primary?: string | null;
  phone_secondary?: string | null;
  email?: string | null;
  email_secondary?: string | null;
  street_address?: string | null;
  unit?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  do_not_contact?: boolean;
}

export interface LinkFields {
  relationship?: string | null;
  is_primary?: boolean;
  is_emergency?: boolean;
  has_authority?: boolean;
  authority_note?: string | null;
  do_not_contact_note?: string | null;
  notes?: string | null;
  started_on?: string | null;
  appointed_date?: string | null;
  court_appointed?: boolean;
  account_ref?: string | null;
}

function post<T>(path: string, getToken: TokenSource, body: unknown) {
  return apiFetch<T>(path, getToken, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/** After any contact write: every client, property and building view of contacts, and the contact itself. */
function useAfterWrite() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['client'] });
    void qc.invalidateQueries({ queryKey: ['contact'] });
    void qc.invalidateQueries({ queryKey: ['property'] });
    void qc.invalidateQueries({ queryKey: ['facilities'] });
    void qc.invalidateQueries({ queryKey: ['contacts'] });
  };
}

export function useContactRoles() {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['contacts', 'roles'],
    queryFn: () => apiFetch<{ roles: ContactRole[]; reasons: EditReasonRow[] }>('/contact-roles', getToken),
    ...queryDefaults,
    staleTime: 30 * 60_000,
  });
}

/** The four reasons, as the server reads them from edit_reason, in its order. */
export function useEditReasons(): EditReasonRow[] {
  return useContactRoles().data?.reasons ?? [];
}

export function useContact(id: string | null) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['contact', id],
    queryFn: () => apiFetch<{ contact: Contact; links: ContactLink[]; may_edit: boolean; may_merge: boolean }>(`/contacts/${id}`, getToken),
    enabled: id !== null,
    ...queryDefaults,
  });
}

export function useContactHistory(id: string | null, enabled: boolean) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['contact', id, 'history'],
    queryFn: () => apiFetch<{ history: HistoryEntry[] }>(`/contacts/${id}/history`, getToken),
    enabled: enabled && id !== null,
    ...queryDefaults,
  });
}

export function useLinkHistory(id: string | null, enabled: boolean) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['contact', 'link', id, 'history'],
    queryFn: () => apiFetch<{ history: HistoryEntry[] }>(`/contact-links/${id}/history`, getToken),
    enabled: enabled && id !== null,
    ...queryDefaults,
  });
}

export function usePropertyContacts(id: string) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['property', id, 'contacts'],
    queryFn: () => apiFetch<{ contacts: ContactLink[]; may_add: boolean }>(`/properties/${id}/contacts`, getToken),
    ...queryDefaults,
  });
}

/** Possible duplicates for what has been typed; asked by the sheet after a pause, never per keystroke. */
export function useCandidates(q: { name: string | null; phone: string | null; email: string | null }) {
  const { getToken } = useSession();
  const any = Boolean(q.name || q.phone || q.email);
  return useQuery({
    queryKey: ['contacts', 'candidates', q],
    queryFn: () => post<{ candidates: Candidate[] }>('/contacts/candidates', getToken, q),
    enabled: any,
    ...queryDefaults,
    staleTime: 60_000,
  });
}

export function useAddLink() {
  const { getToken } = useSession();
  const after = useAfterWrite();
  return useMutation({
    mutationFn: (b: LinkTarget & { role_code: string; contact_id?: string; person?: NewPerson; link?: LinkFields }) =>
      post<{ link_id: string; contact_id: string }>('/contact-links', getToken, b),
    onSuccess: after,
  });
}

export function useEditContact(id: string) {
  const { getToken } = useSession();
  const after = useAfterWrite();
  return useMutation({
    mutationFn: (b: { fields: Partial<NewPerson> & { language?: string | null }; version: string; reason: EditReason; reason_text: string | null }) =>
      post<{ version: number }>(`/contacts/${id}/edit`, getToken, b),
    onSuccess: after,
  });
}

export function useEditLink(id: string) {
  const { getToken } = useSession();
  const after = useAfterWrite();
  return useMutation({
    mutationFn: (b: { fields: LinkFields; version: string; reason: EditReason; reason_text: string | null }) =>
      post<{ version: number }>(`/contact-links/${id}/edit`, getToken, b),
    onSuccess: after,
  });
}

export function useEndLink(id: string) {
  const { getToken } = useSession();
  const after = useAfterWrite();
  return useMutation({
    mutationFn: (b: { ended_on: string | null; reason: EditReason; reason_text: string | null }) =>
      post<{ version: number }>(`/contact-links/${id}/end`, getToken, b),
    onSuccess: after,
  });
}

export function useDuplicates(enabled: boolean) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['contacts', 'duplicates'],
    queryFn: () => apiFetch<{ pairs: { a_id: string; a_name: string; b_id: string; b_name: string; matched_on: string[] }[] }>(
      '/contacts/duplicates', getToken),
    enabled,
    ...queryDefaults,
  });
}

export function useMerge() {
  const { getToken } = useSession();
  const after = useAfterWrite();
  return useMutation({
    mutationFn: (b: { survivor_id: string; retire_id: string; reason_text: string }) =>
      post<{ merge_id: string }>(`/contacts/${b.survivor_id}/merge`, getToken, { retire_id: b.retire_id, reason_text: b.reason_text }),
    onSuccess: after,
  });
}

export function useRoutingPermitted() {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['contact-routing', 'permitted'],
    queryFn: () => apiFetch<{ permitted: boolean }>('/contact-routing/permitted', getToken),
    ...queryDefaults,
    staleTime: 10 * 60_000,
  });
}

/**
 * "Who's calling?" — runs only when asked, never as the person types: every search is recorded against
 * the person asking. A POST, so a phone number or an email never sits in a URL.
 */
export function useRouting(asked: string | null) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['contact-routing', asked],
    queryFn: () => post<{ kind: 'name' | 'phone' | 'email'; contacts: RoutingRow[]; more: boolean }>('/contact-routing', getToken, { q: asked }),
    ...queryDefaults,
    enabled: asked !== null,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
}

/** What "Who's calling?" accepts, as the server decides it (api/src/contacts.ts). */
export function routingQueryProblem(raw: string): string | null {
  const q = raw.trim();
  const words = q.split(/\s+/).filter(Boolean);
  if (words.length === 1 && /^[^@\s]+@[^@\s]+$/.test(q)) return null;
  if (!/\p{L}/u.test(q) && q.replace(/\D/g, '').length >= 7) return null;
  if (q.replace(/[^\p{L}]/gu, '').length >= 3 && words.length <= 6) return null;
  return 'Type three letters of a name, seven digits of a phone number, or an email.';
}
